import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, FileText, Check } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cb900e0f-c443-41f6-9df3-17a2db93c215/artifacts/qd501ira_D70BB6EE-7344-46BE-86BC-6C6D197A54F6.png";

// Sample before/after data with actual images
const samples = [
  {
    id: 1,
    name: 'Jasmine Carter',
    badge: 'Marketing & Communications',
    badgeColor: 'bg-pink-100 text-pink-700',
    description: 'Transformed a basic marketing coordinator resume into a compelling narrative showcasing campaign results, ROI metrics, and leadership potential.',
    beforeImage: '/samples/jasmine_before.png',
    afterImage: '/samples/jasmine_after.png',
    beforePdf: 'https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/e3k454lx_ResumeSurgeon_Sample1_JasmineCarter_BEFORE_v2.pdf',
    afterPdf: 'https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/dn4n15lu_ResumeSurgeon_Sample1_JasmineCarter_FINAL.pdf',
    changes: [
      'Complete content rewrite with metrics',
      'Added quantifiable campaign results',
      'Optimized for marketing manager roles',
      'Enhanced professional summary'
    ]
  },
  {
    id: 2,
    name: 'Miguel Reyes',
    badge: 'Sales & Business Development',
    badgeColor: 'bg-blue-100 text-blue-700',
    description: 'Transformed a generic sales resume into a powerful, metrics-driven document highlighting $2.3M+ in revenue achievements and strategic account management.',
    beforeImage: '/samples/miguel_before.png',
    afterImage: '/samples/miguel_after.png',
    beforePdf: 'https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/zl7ysgo1_ResumeSurgeon_Sample2_MiguelReyes_BEFORE_v2.pdf',
    afterPdf: 'https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/tvoh88ub_ResumeSurgeon_Sample2_MiguelReyes_FINAL.pdf',
    changes: [
      'Restructured for ATS compatibility',
      'Quantified revenue achievements',
      'Optimized keywords for target role',
      'Improved visual hierarchy'
    ]
  },
  {
    id: 3,
    name: 'Aisha Thompson',
    badge: 'Healthcare Administration',
    badgeColor: 'bg-purple-100 text-purple-700',
    description: 'Elevated a healthcare professional\'s resume with quantified achievements, leadership impact, and ATS-optimized formatting for senior administrative roles.',
    beforeImage: '/samples/aisha_before.png',
    afterImage: '/samples/aisha_after.png',
    beforePdf: 'https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/8jqbc26q_ResumeSurgeon_Sample3_AishaThompson_BEFORE_v2.pdf',
    afterPdf: 'https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/axwfbuv9_ResumeSurgeon_Sample3_AishaThompson_FINAL.pdf',
    changes: [
      'Career transition positioning',
      'Highlighted leadership experience',
      'Industry-specific keywords',
      'Achievement-focused bullets'
    ]
  }
];

export default function SamplesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">
              Real Results • Sanitized Samples
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Before & After Gallery
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              See the transformation. These are real (sanitized) examples showing what we can do for your resume.
            </p>
          </div>
        </section>

        {/* Samples */}
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="space-y-16">
            {samples.map((sample) => (
              <Card key={sample.id} className="p-6 md:p-8 rounded-3xl border border-slate-200 overflow-hidden" data-testid={`sample-${sample.id}`}>
                {/* Header */}
                <div className="mb-8 text-center">
                  <Badge className={`${sample.badgeColor} mb-3`}>{sample.badge}</Badge>
                  <h3 className="text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>{sample.name}</h3>
                  <p className="text-slate-600 mt-2 max-w-2xl mx-auto">
                    {sample.description}
                  </p>
                </div>

                {/* Before/After Images */}
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Before */}
                  <a 
                    href={sample.beforePdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="relative">
                      <div className="absolute -top-3 left-4 z-10">
                        <span className="px-4 py-1.5 bg-red-500 text-white text-sm font-bold rounded-full shadow-lg">BEFORE</span>
                      </div>
                      <div className="bg-red-50 rounded-2xl p-3 pt-6 border-2 border-red-200 hover:border-red-400 hover:shadow-lg transition-all">
                        <img 
                          src={sample.beforeImage} 
                          alt={`${sample.name} Resume - Before`} 
                          className="w-full rounded-lg shadow-inner"
                        />
                        <div className="mt-3 text-center">
                          <span className="text-xs text-red-500 flex items-center justify-center gap-1 group-hover:text-red-600">
                            <FileText className="h-3 w-3" /> Click to view full PDF
                          </span>
                        </div>
                      </div>
                    </div>
                  </a>

                  {/* After */}
                  <a 
                    href={sample.afterPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="relative">
                      <div className="absolute -top-3 left-4 z-10">
                        <span className="px-4 py-1.5 bg-teal-500 text-white text-sm font-bold rounded-full shadow-lg">AFTER</span>
                      </div>
                      <div className="bg-teal-50 rounded-2xl p-3 pt-6 border-2 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all">
                        <img 
                          src={sample.afterImage} 
                          alt={`${sample.name} Resume - After`} 
                          className="w-full rounded-lg shadow-inner"
                        />
                        <div className="mt-3 text-center">
                          <span className="text-xs text-teal-500 flex items-center justify-center gap-1 group-hover:text-teal-600">
                            <FileText className="h-3 w-3" /> Click to view full PDF
                          </span>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>

                {/* What Changed */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h4 className="font-semibold text-slate-900 mb-4 text-center">What Changed:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {sample.changes.map((change, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        {change}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 text-center">
          <Card className="p-8 rounded-3xl bg-gradient-hero text-white relative overflow-hidden">
            <div className="logo-badge" style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
              <img src={LOGO_URL} alt="" className="h-8 w-8 rounded-lg" />
            </div>
            <div className="pt-4">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
                Ready for Your Transformation?
              </h3>
              <p className="text-white/80 mb-6">
                Join hundreds of job seekers who've upgraded their resumes with us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/start')}
                  className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
                  data-testid="samples-start-btn"
                >
                  Start Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/audit')}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full"
                  data-testid="samples-audit-btn"
                >
                  Free Quick Audit
                </Button>
              </div>
            </div>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
